import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Loader2, Wand2, RefreshCw, Check, ImageOff, History, Send, Star, Download, Lightbulb, CalendarDays, Shuffle, MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import { isSessionExpired } from '../../utils/adminSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEVELS = ['Débutant', 'Intermédiaire', 'Avancé'];

const EQUIPMENT_OPTIONS = [
  { tag: 'Poids du corps', label: 'Poids du corps' },
  { tag: 'Haltère', label: 'Haltères' },
  { tag: 'Kettlebell', label: 'Kettlebell' },
  { tag: 'Barre', label: 'Barre olympique' },
  { tag: 'Barre EZ', label: 'Barre EZ' },
  { tag: 'Machine', label: 'Machines guidées' },
  { tag: 'Poulie', label: 'Poulie / câble' },
  { tag: 'Medecine ball', label: 'Medecine ball' },
  { tag: 'Accessoire', label: 'Accessoires (élastique, banc...)' },
];

// Miroir léger du mapping backend (_EQUIPMENT_KEYWORDS) pour suggérer les cases à cocher
// probables en live pendant que le coach tape dans le champ libre.
const EQUIPMENT_KEYWORDS = {
  'Haltère': ['haltère', 'haltere', 'dumbbell'],
  'Kettlebell': ['kettlebell', 'kettle bell'],
  'Barre': ['barre olympique', 'barre de musculation', 'barbell'],
  'Barre EZ': ['barre ez', 'ez-bar', 'ez bar'],
  'Machine': ['machine', 'presse à cuisses', 'leg press', 'smith'],
  'Poulie': ['poulie', 'câble', 'cable'],
  'Medecine ball': ['medecine ball', 'médecine ball', 'medicine ball'],
  'Accessoire': ['élastique', 'elastique', 'band', 'trx', 'sangle', 'banc', 'step', 'corde à sauter', 'battle rope', 'ab wheel', 'roue abdo', 'chaise', 'tapis'],
};

const suggestEquipmentTags = (text) => {
  const lower = (text || '').toLowerCase();
  return Object.entries(EQUIPMENT_KEYWORDS)
    .filter(([, keywords]) => keywords.some((kw) => lower.includes(kw)))
    .map(([tag]) => tag);
};

const blankForm = () => ({
  goal: '', days_per_week: 4, level: '', equipment: '', equipmentTags: [], duration_weeks: 8,
  session_duration_minutes: '', extra_notes: '',
});

const estimateSessionMinutes = (session) => {
  const seconds = 180 + (session.exercises || []).reduce(
    (sum, ex) => sum + (ex.sets_count || 0) * (35 + (ex.rest_seconds || 60)), 0,
  );
  return Math.round(seconds / 60);
};

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
// Répartition indicative des séances sur la semaine (juste une visualisation pour le coach,
// le client peut s'entraîner les jours qu'il veut) — espacement le plus régulier possible.
const WEEK_PATTERNS = {
  1: [0], 2: [0, 3], 3: [0, 2, 4], 4: [0, 1, 3, 4], 5: [0, 1, 2, 3, 4], 6: [0, 1, 2, 3, 4, 5], 7: [0, 1, 2, 3, 4, 5, 6],
};
const buildWeekSchedule = (sessionsCount) => {
  const pattern = WEEK_PATTERNS[sessionsCount] || Array.from({ length: Math.min(sessionsCount, 7) }, (_, i) => i);
  const dayToSession = {};
  pattern.forEach((dayIdx, sessionIdx) => { dayToSession[dayIdx] = sessionIdx; });
  return dayToSession;
};

const GOAL_LABELS = { weight_loss: 'Perte de gras', muscle_gain: 'Prise de muscle', maintenance: 'Maintien', recomposition: 'Recomposition corporelle' };
const LEVEL_LABELS = { beginner: 'Débutant', intermediate: 'Intermédiaire', advanced: 'Avancé' };

/** Modal générique : le coach décrit ses contraintes (formulaire guidé + texte libre, ou
 * réutilise un modèle favori en 1 clic), l'IA (Claude Sonnet 4.6) génère un programme complet,
 * puis un ÉCRAN D'APERÇU (durée estimée + matériel + régénération séance par séance avec
 * historique/restauration + envoi au client pour avis) avant validation. Renvoie le brouillon
 * via onGenerated seulement après validation explicite du coach. */
export const AIWorkoutGeneratorModal = ({ csrfToken, onClose, onGenerated, clientEmail }) => {
  const [form, setForm] = useState(blankForm());
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { job_id, program, historyCounts }
  const [regenIdx, setRegenIdx] = useState(null);
  const [regenPanelIdx, setRegenPanelIdx] = useState(null); // index de la séance dont le panneau "Aléatoire / Instructions" est ouvert
  const [regenFeedbackText, setRegenFeedbackText] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateBusy, setTemplateBusy] = useState(false);
  const [historyOpenIdx, setHistoryOpenIdx] = useState(null);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(null);
  const [sendPreviewBusy, setSendPreviewBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [templateSuggestion, setTemplateSuggestion] = useState(null);
  const [suggestingTemplate, setSuggestingTemplate] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-history`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { history: [] }))
      .then((d) => setTemplates((d.history || []).filter((h) => h.is_favorite)))
      .catch(() => setTemplates([]));
  }, []);

  // Préremplit objectif/entraînement depuis le profil "vivant" du client (rempli via son
  // calculateur/onglet Profil) — le coach n'a pas à tout retaper à chaque nouveau programme.
  const profilePrefilledRef = useRef(false);
  useEffect(() => {
    if (!clientEmail || profilePrefilledRef.current) return;
    profilePrefilledRef.current = true;
    fetch(`${BACKEND_URL}/api/admin/programs-pro/client-profile/${encodeURIComponent(clientEmail)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((profile) => {
        if (!profile || !profile.has_profile) return;
        setForm((f) => ({
          ...f,
          goal: f.goal || GOAL_LABELS[profile.goal] || f.goal,
          days_per_week: profile.training_frequency || f.days_per_week,
          level: f.level || LEVEL_LABELS[profile.training_level] || f.level,
          session_duration_minutes: f.session_duration_minutes || (profile.training_duration ? String(profile.training_duration) : f.session_duration_minutes),
        }));
        toast.info('Profil client chargé : objectif et entraînement pré-remplis.');
      })
      .catch(() => {});
  }, [clientEmail]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleEquipmentTag = (tag) => setForm((f) => ({
    ...f, equipmentTags: f.equipmentTags.includes(tag)
      ? f.equipmentTags.filter((t) => t !== tag) : [...f.equipmentTags, tag],
  }));

  const handleEquipmentBlur = () => {
    const suggested = suggestEquipmentTags(form.equipment);
    const newOnes = suggested.filter((t) => !form.equipmentTags.includes(t));
    if (newOnes.length) {
      setForm((f) => ({ ...f, equipmentTags: [...f.equipmentTags, ...newOnes] }));
      toast.info(`Matériel détecté automatiquement : ${newOnes.join(', ')}`);
    }
  };

  const handleGoalBlur = () => {
    if (!form.goal.trim() || templates.length === 0) { setTemplateSuggestion(null); return; }
    setSuggestingTemplate(true);
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-suggest-template`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ goal: form.goal.trim() }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((d) => setTemplateSuggestion(d.suggestion ? d : null))
      .catch(() => setTemplateSuggestion(null))
      .finally(() => setSuggestingTemplate(false));
  };

  const loadTemplate = (idOverride) => {
    const historyId = idOverride || selectedTemplate;
    if (!historyId) return;
    setTemplateBusy(true);
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-generate/from-template/${historyId}`, {
      method: 'POST', credentials: 'include', headers: { 'X-CSRF-Token': csrfToken },
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((d) => setPreview({ job_id: d.job_id, program: d.program, historyCounts: {}, constraints: d.constraints }))
      .catch((r) => { if (r) toast.error('Échec du chargement du modèle.'); })
      .finally(() => setTemplateBusy(false));
  };

  const generate = () => {
    if (!form.goal.trim()) { toast.error("L'objectif est requis."); return; }
    setBusy(true);
    const body = {
      goal: form.goal.trim(),
      days_per_week: +form.days_per_week,
      level: form.level || null,
      equipment: form.equipment.trim() || null,
      equipment_tags: form.equipmentTags.length ? form.equipmentTags : null,
      duration_weeks: +form.duration_weeks,
      session_duration_minutes: form.session_duration_minutes ? +form.session_duration_minutes : null,
      extra_notes: form.extra_notes.trim() || null,
    };
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-generate`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify(body),
    })
      .then((r) => {
        if (isSessionExpired(r)) { setBusy(false); return Promise.reject(null); }
        return r.ok ? r.json() : r.json().then((j) => Promise.reject(j));
      })
      .then((d) => poll(d.job_id))
      .catch((j) => { if (j) toast.error(j?.detail || 'La génération IA a échoué. Réessayez.'); setBusy(false); });
  };

  const poll = (jobId, attempt = 0) => {
    if (attempt >= 60) { setBusy(false); toast.error('La génération prend trop de temps. Réessayez plus tard.'); return; }
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-generate/${jobId}`, { credentials: 'include' })
      .then((r) => {
        if (isSessionExpired(r)) { setBusy(false); return Promise.reject(null); }
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((job) => {
        if (job.status === 'generating') { setTimeout(() => poll(jobId, attempt + 1), 3000); return; }
        setBusy(false);
        if (job.status === 'ready') { setPreview({ job_id: jobId, program: job.program, historyCounts: {}, constraints: job.constraints }); }
        else { toast.error(job.error || 'La génération IA a échoué. Réessayez.'); }
      })
      .catch((r) => { if (r) { setBusy(false); toast.error('Connexion perdue pendant la génération. Réessayez.'); } });
  };

  const regenerateSession = (idx, feedback) => {
    setRegenIdx(idx);
    setRegenPanelIdx(null);
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-generate/${preview.job_id}/regenerate-session`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ session_index: idx, feedback: feedback || undefined }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : r.json().then((j) => Promise.reject(j));
      })
      .then((d) => {
        setPreview((p) => ({ ...p, program: d.program, historyCounts: { ...p.historyCounts, [idx]: d.history_count } }));
        setRegenFeedbackText('');
        toast.success('Séance régénérée !');
      })
      .catch((j) => { if (j) toast.error(j?.detail || 'Échec de la régénération.'); })
      .finally(() => setRegenIdx(null));
  };

  const openSessionHistory = (idx) => {
    if (historyOpenIdx === idx) { setHistoryOpenIdx(null); return; }
    setHistoryBusy(true);
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-generate/${preview.job_id}/session-history/${idx}`, { credentials: 'include' })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((d) => { setHistoryVersions(d.versions || []); setHistoryOpenIdx(idx); })
      .catch((r) => { if (r) toast.error("Échec du chargement de l'historique."); })
      .finally(() => setHistoryBusy(false));
  };

  const restoreVersion = (idx, versionIdx) => {
    setRestoreBusy(versionIdx);
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-generate/${preview.job_id}/restore-session`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ session_index: idx, version_index: versionIdx }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : r.json().then((j) => Promise.reject(j));
      })
      .then((d) => { setPreview((p) => ({ ...p, program: d.program })); setHistoryOpenIdx(null); toast.success('Version restaurée !'); })
      .catch((j) => { if (j) toast.error(j?.detail || 'Échec de la restauration.'); })
      .finally(() => setRestoreBusy(null));
  };

  const sendPreviewToClient = () => {
    setSendPreviewBusy(true);
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-generate/${preview.job_id}/send-preview`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ client_email: clientEmail }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : r.json().then((j) => Promise.reject(j));
      })
      .then(() => toast.success(`Aperçu envoyé à ${clientEmail} pour avis !`))
      .catch((j) => { if (j) toast.error(j?.detail || "Échec de l'envoi de l'aperçu."); })
      .finally(() => setSendPreviewBusy(false));
  };

  const downloadPreviewPdf = () => {
    setPdfBusy(true);
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-generate/${preview.job_id}/pdf`, { credentials: 'include' })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.blob() : Promise.reject(r);
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `apercu-${(preview.program.name || 'programme').replace(/\s+/g, '-').toLowerCase()}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((r) => { if (r) toast.error('Échec du téléchargement du PDF.'); })
      .finally(() => setPdfBusy(false));
  };

  if (preview) {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="ai-preview-screen">
        <div className="bg-zinc-900 border border-violet-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5 shadow-2xl">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" /> Aperçu avant validation
            </h3>
            <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors" data-testid="ai-preview-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white font-semibold mt-2">{preview.program.name}</p>
          <p className="text-xs text-zinc-400 mb-1">{preview.program.goal}</p>
          <p className="text-xs text-zinc-500 mb-4">Matériel demandé : {[...((preview.constraints?.equipment_tags) || []), preview.constraints?.equipment].filter(Boolean).join(', ') || 'salle complète'}</p>

          <div className="mb-4 p-3 bg-zinc-800/40 border border-white/10 rounded-xl" data-testid="ai-preview-week-calendar">
            <p className="text-xs text-zinc-500 mb-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Aperçu de la semaine (répartition indicative)</p>
            <div className="grid grid-cols-7 gap-1.5">
              {WEEKDAYS.map((day, i) => {
                const sessionIdx = buildWeekSchedule(preview.program.sessions.length)[i];
                const hasSession = sessionIdx !== undefined;
                return (
                  <div key={day} title={hasSession ? preview.program.sessions[sessionIdx]?.session_label : 'Repos'}
                    className={`rounded-lg py-2 text-center ${hasSession ? 'bg-violet-500/20 border border-violet-500/40' : 'bg-zinc-900/60 border border-white/5'}`}
                    data-testid={`ai-preview-calendar-day-${i}`}>
                    <p className="text-[10px] text-zinc-500">{day}</p>
                    {hasSession ? <p className="text-[10px] text-violet-300 font-bold mt-0.5">S{sessionIdx + 1}</p> : <p className="text-[10px] text-zinc-500 mt-0.5">Repos</p>}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            {preview.program.sessions.map((s, i) => {
              const minutes = estimateSessionMinutes(s);
              const missingImages = s.exercises.filter((ex) => !ex.image_url).length;
              const histCount = (preview.historyCounts || {})[i] || 0;
              return (
                <div key={i} className="bg-zinc-800/60 border border-white/10 rounded-xl p-3" data-testid={`ai-preview-session-${i}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-white font-semibold text-sm">{s.session_label}</p>
                      <p className="text-xs text-zinc-500" data-testid={`ai-preview-duration-${i}`}>
                        ~{minutes} min estimées · {s.exercises.length} exercice(s)
                        {missingImages > 0 && <span className="text-amber-400" data-testid={`ai-preview-missing-image-${i}`}> · {missingImages} image(s) manquante(s)</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {histCount > 0 && (
                        <button onClick={() => openSessionHistory(i)} disabled={historyBusy}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-white/15 text-zinc-300 text-xs font-semibold rounded-lg hover:border-white/30 transition-colors disabled:opacity-50"
                          data-testid={`ai-preview-history-btn-${i}`}>
                          <History className="w-3.5 h-3.5" /> Historique ({histCount})
                        </button>
                      )}
                      <button onClick={() => { setRegenPanelIdx(regenPanelIdx === i ? null : i); setRegenFeedbackText(''); }} disabled={regenIdx === i}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-violet-500/30 text-violet-300 text-xs font-semibold rounded-lg hover:bg-violet-500/10 transition-colors disabled:opacity-50"
                        data-testid={`ai-preview-regenerate-btn-${i}`}>
                        {regenIdx === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Régénérer cette séance
                      </button>
                    </div>
                  </div>

                  {regenPanelIdx === i && (
                    <div className="mt-2.5 pt-2.5 border-t border-white/10 space-y-2" data-testid={`ai-regenerate-panel-${i}`}>
                      <button onClick={() => regenerateSession(i, null)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/5 border border-white/10 text-zinc-300 text-xs font-semibold rounded-lg hover:bg-white/10 transition-colors"
                        data-testid={`ai-regenerate-random-btn-${i}`}>
                        <Shuffle className="w-3.5 h-3.5" /> Aléatoire (surprends-moi)
                      </button>
                      <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 font-semibold pt-1">
                        <MessageSquarePlus className="w-3.5 h-3.5" /> Ou précise ce que tu veux changer :
                      </div>
                      <textarea
                        value={regenFeedbackText}
                        onChange={(e) => setRegenFeedbackText(e.target.value)}
                        placeholder="Ex: moins d'exercices d'épaules, remplace le développé couché par des pompes lestées, plus axé jambes..."
                        rows={2}
                        className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder:text-zinc-600 focus:border-violet-500/50 focus:outline-none resize-none"
                        data-testid={`ai-regenerate-feedback-input-${i}`}
                      />
                      <button onClick={() => regenerateSession(i, regenFeedbackText)} disabled={!regenFeedbackText.trim()}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-xs font-bold rounded-lg hover:scale-[1.02] transition-transform disabled:opacity-40 disabled:hover:scale-100"
                        data-testid={`ai-regenerate-with-feedback-btn-${i}`}>
                        <RefreshCw className="w-3.5 h-3.5" /> Régénérer avec ces instructions
                      </button>
                    </div>
                  )}

                  {historyOpenIdx === i && (
                    <div className="mt-2 pt-2 border-t border-white/10 space-y-1.5" data-testid={`ai-preview-history-panel-${i}`}>
                      {historyVersions.map((v, vi) => (
                        <div key={vi} className="flex items-center justify-between gap-2 bg-zinc-900/60 rounded-lg px-2 py-1.5">
                          <span className="text-xs text-zinc-400">Version {vi + 1} · {v.exercises.length} exercice(s) · ~{estimateSessionMinutes(v)} min</span>
                          <button onClick={() => restoreVersion(i, vi)} disabled={restoreBusy === vi}
                            className="text-xs text-violet-300 hover:text-violet-200 font-semibold disabled:opacity-50" data-testid={`ai-preview-restore-btn-${i}-${vi}`}>
                            {restoreBusy === vi ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : 'Restaurer'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <ul className="mt-2 space-y-1">
                    {s.exercises.map((ex) => (
                      <li key={ex.exercise_id || ex.name} className="text-xs text-zinc-400 flex items-center gap-2">
                        {ex.image_url ? (
                          <img src={`${BACKEND_URL}${ex.image_url}`} alt={ex.name} loading="lazy" width={28} height={28} className="w-7 h-7 rounded object-cover border border-white/10 flex-shrink-0 bg-zinc-900" />
                        ) : (
                          <ImageOff className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                        )}
                        <span className="text-zinc-300">{ex.name}</span>
                        <span className="text-zinc-600">· {ex.sets_count}×{ex.reps_scheme} · repos {ex.rest_seconds}s</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3 pt-4 flex-wrap">
            <button onClick={() => onGenerated(preview.program)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-bold rounded-xl hover:scale-105 transition-transform"
              data-testid="ai-preview-validate-btn">
              <Check className="w-4 h-4" /> Valider et continuer
            </button>
            {clientEmail && (
              <button onClick={sendPreviewToClient} disabled={sendPreviewBusy}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-sky-500/30 text-sky-300 rounded-xl hover:bg-sky-500/10 transition-colors disabled:opacity-50"
                data-testid="ai-preview-send-client-btn">
                {sendPreviewBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer l&apos;aperçu à la cliente
              </button>
            )}
            <button onClick={downloadPreviewPdf} disabled={pdfBusy}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-emerald-500/30 text-emerald-300 rounded-xl hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
              data-testid="ai-preview-download-pdf-btn">
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF rapide
            </button>
            <button onClick={onClose} className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-zinc-300 rounded-xl hover:border-white/30 transition-colors" data-testid="ai-preview-cancel-btn">
              Annuler
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="ai-workout-modal">
      <div className="bg-zinc-900 border border-violet-500/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-400" /> Générateur de programme IA
          </h3>
          <button onClick={onClose} disabled={busy} className="text-zinc-500 hover:text-white transition-colors" data-testid="ai-workout-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {busy ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3" data-testid="ai-workout-generating">
            <Loader2 className="w-10 h-10 text-violet-400 animate-spin" />
            <p className="text-zinc-300 text-sm font-semibold">Claude construit le programme...</p>
            <p className="text-zinc-500 text-xs">Cela peut prendre jusqu&apos;à 60 secondes pour un programme complet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.length > 0 && (
              <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl" data-testid="ai-template-selector">
                <label className="text-xs text-amber-300 font-semibold mb-1.5 flex items-center gap-1.5">
                  <Star className="w-3.5 h-3.5" fill="currentColor" /> Réutiliser un modèle favori
                </label>
                <div className="flex items-center gap-2">
                  <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="ai-template-select">
                    <option value="">Choisir un modèle...</option>
                    {templates.map((t) => <option key={t.history_id} value={t.history_id}>{t.name}</option>)}
                  </select>
                  <button onClick={() => loadTemplate()} disabled={!selectedTemplate || templateBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold rounded-lg hover:bg-amber-500/30 transition-colors disabled:opacity-50 whitespace-nowrap"
                    data-testid="ai-template-use-btn">
                    {templateBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Utiliser'}
                  </button>
                </div>
                <p className="text-[11px] text-zinc-500 mt-1.5">— ou décrivez un nouveau programme ci-dessous —</p>
              </div>
            )}
            {templateSuggestion?.suggestion && (
              <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center justify-between gap-2 flex-wrap" data-testid="ai-template-suggestion-banner">
                <p className="text-xs text-emerald-300 flex items-center gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5 flex-shrink-0" /> Le modèle <strong>{templateSuggestion.suggestion.name}</strong> semble adapté{templateSuggestion.reason ? ` — ${templateSuggestion.reason}` : ''}.
                </p>
                <button onClick={() => loadTemplate(templateSuggestion.suggestion.history_id)} disabled={templateBusy}
                  className="text-xs font-bold text-emerald-300 hover:text-emerald-200 underline whitespace-nowrap disabled:opacity-50" data-testid="ai-template-suggestion-use-btn">
                  Utiliser ce modèle
                </button>
              </div>
            )}
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Objectif *</label>
              <input value={form.goal} onChange={(e) => set('goal', e.target.value)} onBlur={handleGoalBlur}
                placeholder="Ex: Prise de masse haut du corps, force pure, perte de poids..."
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="ai-goal-input" />
              {suggestingTemplate && <p className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Recherche d&apos;un modèle adapté...</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Jours / semaine</label>
                <input type="number" min={1} max={7} value={form.days_per_week} onChange={(e) => set('days_per_week', e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="ai-days-input" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Niveau</label>
                <select value={form.level} onChange={(e) => set('level', e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="ai-level-select">
                  <option value="">Tous niveaux</option>
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Matériel disponible (cases à cocher)</label>
              <div className="grid grid-cols-2 gap-1.5 mb-2" data-testid="ai-equipment-checkboxes">
                {EQUIPMENT_OPTIONS.map(({ tag, label }) => (
                  <label key={tag} className="flex items-center gap-1.5 text-xs text-zinc-300 bg-zinc-800/60 border border-white/10 rounded-lg px-2 py-1.5 cursor-pointer hover:border-violet-500/40 transition-colors">
                    <input type="checkbox" checked={form.equipmentTags.includes(tag)} onChange={() => toggleEquipmentTag(tag)}
                      className="w-3.5 h-3.5 accent-violet-500" data-testid={`ai-equipment-checkbox-${tag.toLowerCase().replace(/\s+/g, '-')}`} />
                    {label}
                  </label>
                ))}
              </div>
              <input value={form.equipment} onChange={(e) => set('equipment', e.target.value)} onBlur={handleEquipmentBlur}
                placeholder="Précisions libres (ex: kettlebell 8kg, haltère 2kg, poids poignets 1kg...)"
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="ai-equipment-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Durée du programme (sem.)</label>
                <input type="number" min={1} max={52} value={form.duration_weeks} onChange={(e) => set('duration_weeks', e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="ai-duration-input" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Durée séance (min, optionnel)</label>
                <input type="number" min={15} max={180} value={form.session_duration_minutes} onChange={(e) => set('session_duration_minutes', e.target.value)}
                  placeholder="60" className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="ai-session-duration-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Notes complémentaires (libre)</label>
              <textarea value={form.extra_notes} onChange={(e) => set('extra_notes', e.target.value)} rows={3}
                placeholder="Ex: blessure à l'épaule à éviter, préfère les supersets, veut du cardio en fin de séance..."
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none" data-testid="ai-extra-notes-input" />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button onClick={generate} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-bold rounded-xl hover:scale-105 transition-transform" data-testid="ai-generate-submit-btn">
                <Wand2 className="w-4 h-4" /> Générer le programme
              </button>
              <button onClick={onClose} className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-zinc-300 rounded-xl hover:border-white/30 transition-colors" data-testid="ai-generate-cancel-btn">
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIWorkoutGeneratorModal;
